import { Stack } from '@mui/material';
import { FlowOcrBoundingBoxMode, FlowOcrDetailLevel, FlowOcrMethod } from 'flow-schema';

import { type api } from 'common';

import { LegacyOcrFields } from './LegacyOcrFields.tsx';
import { VisionLanguageModelFields } from './VisionLanguageModelFields.tsx';
import { useReactFlowViewContext } from '../../../../context/context';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate.tsx';
import { BlockSelectField } from '../../../ConfigFields/SelectField.tsx';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField.ts';

const methodOptions = [
  { label: 'Vision Language Model', value: FlowOcrMethod.VisionLanguageModel },
  { label: 'Traditional OCR', value: FlowOcrMethod.Legacy },
];

const detailLevelOptions = [
  { label: 'None', value: FlowOcrDetailLevel.None },
  { label: 'Page', value: FlowOcrDetailLevel.Page },
  { label: 'Block', value: FlowOcrDetailLevel.Block },
  { label: 'Paragraph', value: FlowOcrDetailLevel.Paragraph },
  { label: 'Word', value: FlowOcrDetailLevel.Word },
  { label: 'Symbol', value: FlowOcrDetailLevel.Symbol },
];

const boundingBoxModeOptions = [
  { label: 'Disabled', value: FlowOcrBoundingBoxMode.None },
  { label: 'Enabled', value: FlowOcrBoundingBoxMode.All },
];

export function OcrV2BlockFormFields() {
  const { value: method } =
    useSelectedNodeField<api.FlowDefinition.OcrV2Block['config']['method']>('config.method');

  const { value: prompt } =
    useSelectedNodeField<api.FlowDefinition.OcrV2Block['config']['prompt']>('config.prompt');

  const { value: detailLevel } =
    useSelectedNodeField<api.FlowDefinition.OcrV2Block['config']['detailLevel']>(
      'config.detailLevel'
    );

  const { value: boundingBoxMode, setValue: setBoundingBoxMode } =
    useSelectedNodeField<api.FlowDefinition.OcrV2Block['config']['boundingBoxMode']>(
      'config.boundingBoxMode'
    );

  const { isEditable } = useReactFlowViewContext();

  const detailLevelSupportsBoundingBoxes = ![FlowOcrDetailLevel.None, FlowOcrDetailLevel.Page].some(
    (level) => level === detailLevel
  );

  // FIXME: Existing code mutates state during render which can cause bugs.
  // Refactor to have a fallback value, and explicitly set it only when the user makes a
  // change via onClick or onChange (Or do a db migration.)
  if (!detailLevelSupportsBoundingBoxes && boundingBoxMode === FlowOcrBoundingBoxMode.All) {
    setBoundingBoxMode(FlowOcrBoundingBoxMode.None);
  }

  return (
    <Stack spacing={2}>
      <BlockValueTemplateField
        fieldName="config.fileReference"
        label="File reference"
        helperText="A string in the format {type}:{identifier}."
      />

      <BlockSelectField
        options={methodOptions}
        label="Processing method"
        fieldName="config.method"
        defaultValue={FlowOcrMethod.Legacy}
        clearable={false}
        helperText={
          method === FlowOcrMethod.VisionLanguageModel
            ? 'Uses Anthropic Claude Opus 4.6 for highest accuracy on checkboxes, handwriting, and complex layouts.'
            : 'Uses Google Vision API for deterministic text extraction with optional bounding box metadata.'
        }
      />

      {method === FlowOcrMethod.VisionLanguageModel ? (
        <VisionLanguageModelFields isEditable={isEditable} prompt={prompt} />
      ) : (
        <LegacyOcrFields
          detailLevelSupportsBoundingBoxes={detailLevelSupportsBoundingBoxes}
          detailLevelOptions={detailLevelOptions}
          boundingBoxModeOptions={boundingBoxModeOptions}
        />
      )}
    </Stack>
  );
}
